import React, { useMemo, useRef, useState, useEffect } from "react";
import { ReactComponent as ArrowLefIcon } from "./../../images/svg/arrowLeft.svg";
import { ReactComponent as ArrowRightIcon } from "./../../images/svg/arrowRight.svg";
import { ReactComponent as LoaderIcon } from "./../../images/svg/loader.svg";
import { gettext } from "../../adapter";
import Star from "./Star";
import DaySelector from "./DaySelector";
import Day from "./Day";
import { signedNumberString } from "../../utils/globalUtils";
import { useSelector } from "react-redux";
import { parseRegion } from "../../utils/weatherForecastUtils";

// CSS-scroll-snap-based horizontal strip that replaces Swiper. Arrows scroll
// the container by one slide width; `atStart`/`atEnd` track reachability so
// the disabled styling matches the old behaviour.
const ForecastStrip = ({ children }) => {
  const ref = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = () => {
    const el = ref.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 0);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  const scrollBy = (sign) => {
    const el = ref.current;
    if (!el) return;
    const slide = el.firstElementChild;
    const step = slide ? slide.clientWidth + 15 : el.clientWidth * 0.8;
    el.scrollBy({ left: sign * step, behavior: "smooth" });
  };

  return (
    <div className="dacom-strip">
      <div className="weather--navigationdashboard">
        {/* eslint-disable-next-line */}
        <a
          className={`button--neutral button-prev is-small is-left ${atStart ? "is-disabled" : ""}`}
          onClick={() => scrollBy(-1)}
        >
          <ArrowLefIcon />
        </a>
        {/* eslint-disable-next-line */}
        <a
          className={`button--neutral button-next is-small is-right ${atEnd ? "is-disabled" : ""}`}
          onClick={() => scrollBy(1)}
        >
          <ArrowRightIcon />
        </a>
      </div>
      <div className="dacom-strip__track" ref={ref}>
        {children}
      </div>
    </div>
  );
};

const WeatherForecastOverview = () => {
  const [perDay, setPerday] = useState(false);

  const regionState = useSelector((state) => state.weatherRegion);

  const { region, days, days24, isFetching } = useMemo(() => {
    if (!regionState.region) return { ...regionState };
    return { ...regionState, ...parseRegion(regionState.region) };
  }, [regionState]);

  if (!days || !days24) return null;

  const preloaderClass = isFetching ? "object-preloader is-loading" : "object-preloader is-loaded";

  const timezoneOffsetBrowser = new Date().getTimezoneOffset();
  if (region.timezoneOffset !== timezoneOffsetBrowser) {
    console.warn(
      "You are watching a sensor for a different timezone (" +
        signedNumberString(region.timezoneOffset / 60) +
        "h) then yours (" +
        signedNumberString(timezoneOffsetBrowser / 60) +
        "h)"
    );
  }

  return (
    <div className="l-full">
      <div className="panel has-object-preloader">
        <div className={preloaderClass}>
          <div className="object-preloader--content">
            <LoaderIcon className="loader" />
          </div>
        </div>
        <h2 className="little-spacing">
          {gettext("Weather forecast")}
          <small>{region.name}</small>
          <Star region={region} />
          <DaySelector perDay={perDay} setPerDay={setPerday} />
        </h2>
        {perDay && (
          <ForecastStrip>
            {days24
              .filter((day) => day.forecasts.length === 1)
              .map((day) => (
                <div className="dacom-strip__slide dacom-strip__slide--narrow" key={day.date}>
                  <Day day={day} view="advanced" width="--24" />
                </div>
              ))}
          </ForecastStrip>
        )}
        {!perDay && (
          <ForecastStrip>
            {days
              .filter((day) => day.forecasts.length === 4)
              .map((day) => (
                <div className="dacom-strip__slide dacom-strip__slide--wide" key={day.date}>
                  <Day day={day} view="advanced" width="" />
                </div>
              ))}
          </ForecastStrip>
        )}
      </div>
    </div>
  );
};

export default WeatherForecastOverview;
